import { faSearch, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { ComponentPropsWithoutRef, KeyboardEvent, useEffect, useState } from "react";

import Icon from "src/components-v2/Icon";
import { ClearButton, SearchButton, SearchField, SearchFieldWrapper, SearchWrapper } from "./SearchField.styled";

import { getTranslationManually } from "src/helpers/locale";
import { PropsWithDataQaid } from "src/types/componentProps";

export type SearchFieldProps = PropsWithDataQaid<{
  /**
   * disabled status of search button
   */
  disabled?: boolean;

  /**
   * Search data from parent state, useful if parent needs current state of search query
   */
  searchState?: string;

  /**
   * Function to set parent search state
   */
  setSearchState?: (value: string) => void;

  /**
   * callback when clearing the text
   */
  onClearText?: () => void;

  /**
   * Handler for searching query. pass searchQuery data to be used on parent
   */
  onSearch?: Function;

  /**
   * Search field placeholder
   */
  placeholder?: string;

  /**
   * boolean wether arguments for onSearch props type is object or not.
   * useful if in search function need conditionals between using new searchQuery or parent query, e.g
   *
   * const = (searchQuery) => fetch({ query: searchQuery ? searchQuery.value : parentQuery });
   *
   * if we just do searchQuery || parentQuery , when clearing query, the parentQuery will be used
   * since provided searchQuery is falsy
   */
  objectArg?: boolean;

  /**
   * boolean prop when this Component is used inside FieldGroup with FieldGroup.Prepend
   * this prop will styling the left top & bottom border radius
   */
  withPrepend?: boolean;

  buttonSearchText?: string;

  outline?: boolean;

  /**
   * Value prop to control the input value
   */
  value?: string;

  searchButtonClass?: ComponentPropsWithoutRef<"button">["className"];
  searchFieldClass?: ComponentPropsWithoutRef<"input">["className"];
}>;

const defaultProps = {
  disabled: false,
  searchState: "",
  setSearchState: () => {},
  onSearch: () => {},
  onClearText: () => {},
  placeholder: "",
  "data-qaid": "qa-search-field-default-qaid",
  objectArg: false,
  withPrepend: false,
  outline: false
};

function ClearableSearchField(props: SearchFieldProps) {
  const {
    disabled,
    searchState,
    setSearchState,
    onClearText,
    onSearch,
    placeholder,
    "data-qaid": dataQaid,
    objectArg,
    withPrepend,
    outline,
    buttonSearchText,
    searchButtonClass,
    searchFieldClass,
    value
  } = props;

  const [searchQuery, setSearchQuery] = useState("");
  const [searched, setSearched] = useState(false);

  function clearField() {
    setSearchQuery("");
    if (searched) {
      const value = objectArg ? { value: "" } : "";
      onSearch(value);
      setSearchState("");
      setSearched(false);
    }
    onClearText();
  }

  function search() {
    if (searched || searchQuery) {
      const value = objectArg ? { value: searchQuery } : searchQuery;
      onSearch(value);
      setSearchState(searchQuery);
      setSearched(true);
    }
  }

  function handleKeyUp(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      search();
    }
  }

  useEffect(() => {
    setSearchQuery(searchState);
    if (searchState) {
      setSearched(true);
    }
  }, [searchState]);

  useEffect(() => {
    if (!!value) {
      setSearchQuery(value);
    }
  }, [value]);

  return (
    <SearchWrapper>
      <SearchButton
        className={searchButtonClass}
        outline={outline}
        variant="secondary"
        icon={faSearch}
        onClick={search}
        disabled={disabled}
        data-qaid={dataQaid + "-search-button"}
      >
        {buttonSearchText !== undefined ? buttonSearchText : getTranslationManually("Cari", "Search")}
      </SearchButton>
      <SearchFieldWrapper>
        <SearchField
          className={searchFieldClass}
          withPrepend={withPrepend}
          data-qaid={dataQaid}
          value={searchQuery}
          placeholder={placeholder}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          disabled={disabled}
          onKeyUp={handleKeyUp}
        />
        {searchQuery.length > 0 && (
          <ClearButton onClick={clearField} disabled={disabled}>
            <Icon icon={faTimesCircle} />
          </ClearButton>
        )}
      </SearchFieldWrapper>
    </SearchWrapper>
  );
}

ClearableSearchField.defaultProps = defaultProps;

export default ClearableSearchField;
